import { createFileRoute, redirect } from "@tanstack/react-router";
import { AuthForm } from "@/components/AuthForm";
import { ErrorBoundary } from "@lato/client/components/ErrorBoundary";
import { signInWithMicrosoft, signInWithGoogle, signInWithWorkOS } from "@/lib/web-auth";
import { sessionReady } from "@/lib/auth-ready";
import { getSessionSnapshot, supabase } from "@lato/client/lib/supabase";
import { clearAuthBlock, readAuthBlock } from "@lato/client/lib/authBlock";
import { apiRequest, AccountDeactivatedError, SessionExpiredError, SsoRequiredError } from "@lato/client/lib/apiClient";
import { safeSignInReturnPath } from "@/lib/sign-in-redirect";
import { isSignedOutError, queryClient } from "@/lib/queryClient";
import { tokenOrgId } from "@/lib/sessionGuards";
import { cloudKeys, fetchFreshMe } from "@/hooks/useCloudChat";

/**
 * OAuth failures (e.g. the invite-only signup gate) come back as `?error=…` /
 * `#error=…` on this page, because the sign-in flow sets its `redirectTo` to
 * `/signin`. Read `error_description` straight from the page's own URL.
 */
function readOAuthError(): string | undefined {
  for (const part of [window.location.search, window.location.hash]) {
    if (part.length <= 1) continue;
    const desc = new URLSearchParams(part.slice(1)).get("error_description");
    if (desc) return desc;
  }
  return undefined;
}

function SignIn() {
  const { redirect: destination } = Route.useSearch();
  const returnPath = safeSignInReturnPath(destination);
  const callback = returnPath ? `/signin?${new URLSearchParams({ redirect: returnPath })}` : "/signin";
  const goToLanding = () => {
    window.location.href = import.meta.env.VITE_LANDING_URL;
  };

  return (
    <ErrorBoundary name="AuthForm">
      <AuthForm
        onBack={goToLanding}
        // Return OAuth to /signin itself: failures land here at top-level so we
        // can show them; success falls through beforeLoad on to the app.
        onMicrosoftSignIn={() => signInWithMicrosoft(callback)}
        onGoogleSignIn={() => signInWithGoogle(callback)}
        onSsoSignIn={(org) => signInWithWorkOS(org, callback)}
        initialError={readOAuthError()}
      />
    </ErrorBoundary>
  );
}

export const Route = createFileRoute("/signin")({
  validateSearch: (search: Record<string, unknown>) => ({
    redirect: safeSignInReturnPath(search.redirect),
  }),
  beforeLoad: async ({ search }) => {
    await sessionReady;
    if (getSessionSnapshot()) {
      const pending = readAuthBlock();
      try {
        // First-login placement activates pending/domain memberships. The
        // strict switch endpoint cannot enter a membership that is still pending.
        const me = await fetchFreshMe(queryClient).catch((error: unknown) => {
          if (isSignedOutError(error)) throw error;
          throw new Error("Unable to load your workspace. Please contact your administrator.");
        });
        if (pending?.code === "sso_required" && pending.orgId) {
          // The original switch may have failed before the user's preferred
          // workspace changed. Retry only after authentication; the backend
          // rechecks membership and the new session's actual SSO company.
          const response = await apiRequest("/users/me/current-org", {
            method: "PUT",
            body: JSON.stringify({ org_id: pending.orgId }),
          });
          if (!response.ok) throw new Error("Unable to enter this workspace. Please contact your administrator.");
        }
        // Placement can change the acting organization even without an explicit
        // switch. Refresh before storage/API calls use the original login token.
        const { data, error } = await supabase.auth.refreshSession();
        if (error) throw error;
        // Route guards trust a cached answer whatever its age, so keep this one only while the
        // new token still acts in the workspace it names. Otherwise the next reader asks again.
        if (tokenOrgId(data.session) !== (me.organization?.id ?? null)) {
          queryClient.removeQueries({ queryKey: cloudKeys.me });
        }
      } catch (error) {
        if (error instanceof SsoRequiredError || error instanceof SessionExpiredError ||
          error instanceof AccountDeactivatedError) return;
        throw error;
      }
      clearAuthBlock();
      throw redirect({ to: safeSignInReturnPath(search.redirect) || "/" });
    }
  },
  component: SignIn,
});
